/**
 * useGovtOpportunityStats – Govt opportunity stats for Home scorecard (KTU-001)
 * Uses the same Redis-cached /api/opportunities/dashboard-stats endpoint as
 * OpportunitiesDashboard so counts (especially "Next 30 days") stay aligned.
 */

import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '../api/client';
import { queryEntities } from '../api/entities';

export interface GovtOpportunityStats {
  watching: number;
  bidding: number;
  won: number;
  lost: number;
  upcomingDeadlines: number;
  openOpportunities: number;
  trackedOpportunities: number;
}

interface DashboardStatsResponse {
  total: number;
  open_count: number;
  tracked_count: number;
  upcoming_deadlines_count: number;
  cached_at: string | null;
}

async function countByTrackingStatus(projectId: string, trackingStatus: string): Promise<number> {
  const result = await queryEntities({
    project_id: projectId,
    entity_type: 'govt_opportunity',
    filters: { tracking_status: trackingStatus },
    limit: 1,
    offset: 0,
  });
  return result.total_count ?? 0;
}

export function useGovtOpportunityStats(projectId: string | null) {
  return useQuery({
    // Separate cache key from OpportunitiesDashboard (different response shape)
    queryKey: ['govt-opportunity-stats-home', projectId],
    queryFn: async (): Promise<GovtOpportunityStats> => {
      if (!projectId) throw new Error('Project ID required');
      const api = getAPIClient();
      const [dashboardStats, watching, bidding, won, lost] = await Promise.all([
        api.get<DashboardStatsResponse>(`/api/opportunities/dashboard-stats?project_id=${projectId}`),
        countByTrackingStatus(projectId, 'watching'),
        countByTrackingStatus(projectId, 'bidding'),
        countByTrackingStatus(projectId, 'won'),
        countByTrackingStatus(projectId, 'lost'),
      ]);
      return {
        watching,
        bidding,
        won,
        lost,
        upcomingDeadlines: dashboardStats.upcoming_deadlines_count,
        openOpportunities: dashboardStats.open_count,
        trackedOpportunities: dashboardStats.tracked_count,
      };
    },
    enabled: !!projectId,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
